// Eye Follow on the splat rig path (ENG-1060).
//
// A splat character's eyes can only move through its RIG: the eye-aim controls
// (`CTRL_L_eye.translateX/Y`, `CTRL_R_eye.translateX/Y`) rotate the eyeballs —
// exactly under ORL (RigLogic drives the FACIAL_L/R_Eye joints, and the eye
// shells ride them), approximately under rig2mesh — and the decoders re-texture
// whatever the geometry does.
//
// WHY THIS RUNS AFTER THE CLAMP AND THE PROJECTION, and must keep doing so.
// isaac-v6's pose table freezes every eye control — std exactly 0.0000 across
// all 137 trained poses for all 9 of them (the capture authored gaze through
// Maya lookAt controls, outside the rig) — so `trainedLimits` clamps any eye
// delta straight back to rest, and the mouth-subspace projection's basis has
// zero variance there and erases whatever survives. That pair is the measured
// mechanism of the first attempt's "maxRigDelta 0.000000". Writing the eye
// controls AFTER both stages trades that erasure for extrapolation on exactly
// these four controls, bounded by the bundle's DECLARED rig_range — a bounded,
// deliberate exception, verified acceptable on pixels at the deltas the node's
// maxYaw/maxPitch normalization can produce.
//
// THE SIGN CONVENTION IS MEASURED, NOT INHERITED — and deliberately NOT
// arkitToMh's. Measured on isaac-v6 (pupil-centroid over single-control
// probes, 2026-08-27): the GUI board's `translateX` is an ABSOLUTE direction —
// `+` looks toward the CHARACTER'S LEFT for BOTH eyes — and `translateY +`
// looks UP. arkitToMh's eye rows are written in ARKit's mirrored In/Out terms
// (r[38] = In_L − Out_L, r[112] = In_R − Out_R), which lands the LEFT eye
// BACKWARDS under the absolute convention; shipped that way, the (stronger)
// left eye dominated and the gaze visibly aimed AWAY from the camera. Those
// rows were never validated against this board: isaac's training data froze
// the eye controls, so the trained pipeline never exercised them, and eyeline's
// pose table came from the capture solve, not from arkitToMh. arkitToMh itself
// stays untouched — it is the canonical trained mapping for everything else.
//
// Also measured, for whoever debugs "his right eye barely moves": under the
// DEFAULT rig2mesh, isaac-v6's right-eye horizontal response is ~5x weaker
// than the left's (±1.5 px vs ±9.8 px for the same ±0.35 control delta) — a
// distillation-extrapolation artifact of the frozen training controls, not a
// mapping bug. `?rig=orl` drives the eye shells from real RigLogic joints and
// does not have that asymmetry.
//
// Pure + dependency-light on purpose (node --test importable): the caller owns
// the THREE scratch and the world transform; this module owns only the
// name→slot resolution and the per-frame arithmetic. Nothing here allocates
// per frame.

import {
  EYE_LOOK_DOWN_LEFT, EYE_LOOK_IN_LEFT, EYE_LOOK_OUT_LEFT, EYE_LOOK_UP_LEFT,
} from '../../lib/eyeAim.js'

// slot resolution: bundle control name → which SIGNED AXIS of the aim it
// spends, on the measured absolute board convention (+yaw = character's left,
// +pitch = up — the same signs eyeAim.js's angles carry).
//
// THE SET IS C_eye + L_eye + R_eye, MEASURED PER CHARACTER (pupil-centroid
// probes, 2026-08-27), because the two shipped bundles trained their gaze on
// DIFFERENT controls and each responds only where its training moved:
//   - isaac-v6 froze everything; its rig2mesh responds strongly to the
//     PER-EYE controls (L ±7-10 px per ±0.35) and its C_eye rests near the
//     range floor (0.077), so C contributes little there.
//   - eyeline-v10c's capture drove gaze through CTRL_C_eye (full 0-1 span
//     across its 246 poses); per-eye alone moved its pupils ~1 px where
//     C+L+R together moved them ~2.4 px conjugate.
// Driving all six lets each bundle answer through whichever controls its
// training covered, with the per-control range clamp absorbing the rest.
// CTRL_L/R_eyeAim are deliberately ABSENT: probed on eyeline (the bundle that
// trains them, std up to 0.39), aim-only deltas moved the pupils 0.0-0.2 px —
// dead weight — and on the shipped DNAs they have no GUI counterpart anyway
// (OrlDeform logs them unmapped).
const EYE_CONTROL_AXES = [
  ['CTRL_C_eye.translateX', 'yaw'],
  ['CTRL_L_eye.translateX', 'yaw'],
  ['CTRL_R_eye.translateX', 'yaw'],
  ['CTRL_C_eye.translateY', 'pitch'],
  ['CTRL_L_eye.translateY', 'pitch'],
  ['CTRL_R_eye.translateY', 'pitch'],
]

/**
 * Resolve which slots of a bundle's control space the eye aim writes.
 *
 * Every slot carries a SLOPE: the direct convention uses ±1 (one aim unit =
 * one control unit on the measured absolute convention), the trained mode
 * below replaces this set with regression slopes.
 *
 * @param {string[]} rigNames - the bundle's rig_names.json
 * @returns {{mode:'direct', slots: {slot:number, axis:'yaw'|'pitch', slope:number}[]}|null}
 *   null when the control space has none of the six (the caller reports the
 *   node unavailable rather than leaving it looking functional)
 */
export function resolveEyeFollowSlots(rigNames) {
  if (!Array.isArray(rigNames) || rigNames.length === 0) return null
  const slots = []
  for (const [name, axis] of EYE_CONTROL_AXES) {
    const slot = rigNames.indexOf(name)
    if (slot !== -1) slots.push({ slot, axis, slope: 1 })
  }
  return slots.length ? { mode: 'direct', slots } : null
}

// Trained-drive curation: gaze must never author a lid CLOSURE — the
// procedural blink overlay and blinkMacro own the lids, and a gaze that adds
// blink/lidPress deltas fights them (and reads as squinting at whoever you
// look at). Everything else inside the eye region rides along: a trained
// glance IS a whole configuration (aim, parallelLook, lash tweakers, the
// lookAt/convergence switches), and pruning it to "clean-looking" subsets
// measurably kills the response.
const GAZE_DRIVE_EXCLUDE = /_blink\.|_lidPress\./
const BLINK_ONLY = /_blink\./

// The regression label per axis — the animator's own gaze widget. A bundle
// whose capture drove gaze through the rig moves these across its pose table
// (eyeline: tx spans the full declared range); one that authored gaze outside
// the rig (isaac: Maya lookAt) leaves them frozen and gets the direct mode.
const GAZE_LABELS = [
  ['CTRL_C_eye.translateX', 'yaw'],
  ['CTRL_C_eye.translateY', 'pitch'],
]

const MIN_LABEL_STD = 0.05     // below this the label is frozen — no gaze was trained
const MIN_REF_HEADROOM = 0.05  // a reference this close to base has nothing to offer
const OPEN_EYE_BLINK_MAX = 0.3 // reference rows must have OPEN eyes

/**
 * TRAINED gaze drive: piecewise interpolation of the EYE-REGION block from the
 * bundle's rest pose toward base-conditioned trained REFERENCE rows, one per
 * aim direction — the gaze sibling of `buildLiveMouthDrive`'s closed/open
 * references.
 *
 * Why endpoints and not regression slopes (the first attempt): a bundle that
 * trained its gaze as a correlated configuration barely answers single
 * controls, and a LINEAR model fit around the base undershoots what a real
 * trained extreme looks like — measured on eyeline-v10c, slopes reached
 * 3-4.6 px of pupil travel where decoding the trained extreme rows themselves
 * showed a 12.7 px ceiling. Interpolating the eye-region block toward those
 * rows reproduced ~13.8 px at t=1 with the lids kept from base (verified on
 * pixels) — on-manifold at full aim BY CONSTRUCTION, because t=1 lands on a
 * captured configuration (minus the excluded lid controls).
 *
 * Reference picking is base-conditioned, like `pickMouthReference`: among the
 * open-eyed rows near the driven label's extreme in that direction, the row
 * nearest the BASE over the rest of the eye region wins — so a hard-left
 * reference carries as little foreign vertical gaze and squint as the table
 * allows. Scope stays the bundle's own `eyes_idx` region list, so a
 * co-occurrence artifact can never make gaze move the mouth (the rigBoard.js
 * companion-set trap), and blink/lidPress are excluded by name (see above).
 * A direction with no trained travel (eyeline can't look DOWN — its C_eye.ty
 * rests on the range floor) gets no reference and stays at rest, which is
 * the truth of the data rather than an extrapolated guess.
 *
 * @param {string[]} rigNames  - the bundle's rig_names.json
 * @param {Float32Array|number[]} presets - the pose table, rows x dim, flat
 * @param {number} dim - controls per row
 * @param {number[]} eyesIdx - the bundle's own eye-region slot list
 *   (scene.json mouth_controller.eyes_idx)
 * @param {Float32Array|number[]} base - the rest configuration the live path
 *   composes onto (arkitBase) — deltas anchor here so aim 0 is a no-op
 * @returns {{mode:'trained', refs:object, slots:{slot:number, axis:string, dPos:number, dNeg:number}[]}|null}
 *   null when there is no usable table, no base, or the labels are frozen —
 *   the caller falls back to the direct convention.
 */
export function buildGazeDrive(rigNames, presets, dim, eyesIdx, base) {
  if (!Array.isArray(rigNames) || !presets || !dim || presets.length < dim * 8) return null
  if (!Array.isArray(eyesIdx) || eyesIdx.length === 0) return null
  if (!base || base.length !== dim) return null
  const rows = Math.floor(presets.length / dim)

  const blinkSlots = eyesIdx.filter((s) => BLINK_ONLY.test(rigNames[s] ?? ''))
  const region = eyesIdx.filter((s) => s >= 0 && s < dim && !GAZE_DRIVE_EXCLUDE.test(rigNames[s] ?? ''))
  if (!region.length) return null

  const openRows = []
  for (let r = 0; r < rows; r++) {
    let open = true
    for (const b of blinkSlots) if (presets[r * dim + b] > OPEN_EYE_BLINK_MAX) { open = false; break }
    if (open) openRows.push(r)
  }
  if (!openRows.length) return null

  // Base-conditioned reference: among the open-eyed rows within 0.1 of the
  // label's extreme toward `direction`, the one nearest base over the rest of
  // the eye region.
  const pickRef = (label, direction) => {
    let extreme = base[label]
    for (const r of openRows) {
      const v = presets[r * dim + label]
      if (direction * (v - extreme) > 0) extreme = v
    }
    if (direction * (extreme - base[label]) < MIN_REF_HEADROOM) return null // no trained travel this way
    let best = null, bestD = Infinity
    for (const r of openRows) {
      if (direction * (extreme - presets[r * dim + label]) > 0.1) continue
      let d = 0
      for (const s of region) {
        if (s === label) continue
        const diff = presets[r * dim + s] - base[s]
        d += diff * diff
      }
      if (d < bestD) { bestD = d; best = r }
    }
    return best
  }

  const labelStd = (label) => {
    let m = 0
    for (let r = 0; r < rows; r++) m += presets[r * dim + label]
    m /= rows
    let v = 0
    for (let r = 0; r < rows; r++) { const d = presets[r * dim + label] - m; v += d * d }
    return Math.sqrt(v / rows)
  }

  const perSlot = new Map() // "slot:axis" -> {slot, axis, dPos, dNeg}
  const refs = { yawPos: null, yawNeg: null, pitchPos: null, pitchNeg: null }
  for (const [labelName, axis] of GAZE_LABELS) {
    const label = rigNames.indexOf(labelName)
    if (label === -1 || labelStd(label) < MIN_LABEL_STD) continue
    const refPos = pickRef(label, +1) // +aim = char-left / up (the label rises)
    const refNeg = pickRef(label, -1)
    refs[`${axis}Pos`] = refPos
    refs[`${axis}Neg`] = refNeg
    if (refPos === null && refNeg === null) continue
    for (const slot of region) {
      const dPos = refPos !== null ? presets[refPos * dim + slot] - base[slot] : 0
      const dNeg = refNeg !== null ? presets[refNeg * dim + slot] - base[slot] : 0
      if (dPos === 0 && dNeg === 0) continue
      perSlot.set(`${slot}:${axis}`, { slot, axis, dPos, dNeg })
    }
  }
  const slots = [...perSlot.values()]
  return slots.length ? { mode: 'trained', refs, slots } : null
}

/**
 * One resolver for the pipeline: the trained drive when the bundle's pose
 * table supports one, else the direct measured convention.
 */
export function resolveEyeFollowDrive({ rigNames, presets = null, dim = 0, eyesIdx = null, base = null } = {}) {
  return buildGazeDrive(rigNames, presets, dim, eyesIdx, base) ?? resolveEyeFollowSlots(rigNames)
}

/**
 * Fold the eye aim into an already-composed rig vector, in place.
 *
 * `overlay52` is `eyeFollowRuntime.arkitOverlay(...)` — the 8 EyeLook channels
 * and nothing else. The LEFT-eye channel quartet is read back into the signed
 * yaw/pitch it encodes (arkitEyeChannels writes both eyes from the ONE aim, so
 * one side carries the full signal), then spent on the measured absolute
 * convention above. Clamped to the DECLARED range only — see the header for
 * why the trained limits must not apply here.
 *
 * @param {Float32Array} rig - composed rig, mutated
 * @param {{slots: {slot:number, axis:string}[]}} resolved
 * @param {ArrayLike<number>} overlay52
 * @param {number} lo - declared rig_range low  (pass -Infinity when undeclared)
 * @param {number} hi - declared rig_range high (pass +Infinity when undeclared)
 */
export function applyEyeFollowRig(rig, resolved, overlay52, lo, hi) {
  // + = character's left (Out on the left eye is away from the nose = left).
  const yaw = overlay52[EYE_LOOK_OUT_LEFT] - overlay52[EYE_LOOK_IN_LEFT]
  // + = up.
  const pitch = overlay52[EYE_LOOK_UP_LEFT] - overlay52[EYE_LOOK_DOWN_LEFT]
  for (const e of resolved.slots) {
    const aim = e.axis === 'yaw' ? yaw : pitch
    // Direct mode is a symmetric slope; the trained mode is PIECEWISE — each
    // aim sign interpolates toward its own reference row (aim +1/-1 lands
    // exactly on that trained configuration, minus the lid exclusions).
    const delta = e.slope !== undefined
      ? e.slope * aim
      : (aim >= 0 ? aim * e.dPos : -aim * e.dNeg)
    const v = rig[e.slot] + delta
    rig[e.slot] = v < lo ? lo : v > hi ? hi : v
  }
}

/**
 * The aim ORIGIN for a splat bundle, in checkpoint space: the eyes' midpoint,
 * approximated as the vertex mean of the small non-head branch that sits
 * HIGHEST — eyes sit above teeth on every face. Measured on isaac-v6: its
 * 'body' branch is the eye region (two 321-vert eyeball shells + lash shells,
 * bounds a 10x3x3 cm box at eye height), mean [-1.56, 41.26, 12.64] cm, while
 * the teeth branch's mean sits 7 cm lower — so the rule picks the eyes.
 *
 * Fallback with no such branch: the head branch's own vertex mean — a
 * DOCUMENTED approximation (isaac: 4.4 cm below and 3 cm behind the eyes,
 * which at a half-metre camera is ~5 degrees of aim error).
 *
 * Load-time only (one pass over every branch's verts); never per frame.
 *
 * @param {{name:string, verts:Float32Array|null}[]} branches
 * @returns {[number,number,number]|null} checkpoint-space midpoint, or null
 *   when no branch has vertices at all
 */
export function splatEyePivot(branches) {
  const stats = []
  for (const b of branches ?? []) {
    const v = b?.verts
    if (!v || v.length < 3) continue
    let x = 0, y = 0, z = 0
    const n = v.length / 3
    for (let i = 0; i < v.length; i += 3) { x += v[i]; y += v[i + 1]; z += v[i + 2] }
    stats.push({ name: b.name, n, mean: [x / n, y / n, z / n] })
  }
  if (!stats.length) return null
  let head = stats[0]
  for (const s of stats) if (s.n > head.n) head = s
  // Small relative to the head, or it IS the head-scale region and its mean is
  // no better than the head's own.
  const candidates = stats.filter((s) => s !== head && s.n < head.n * 0.5)
  if (!candidates.length) return head.mean
  let best = candidates[0]
  for (const s of candidates) if (s.mean[1] > best.mean[1]) best = s
  return best.mean
}
